import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import useContext from '../../../../../hooks/useContext';
import { ProductContext } from '../ProductProvider';
import { IImage } from '../useParseProduct';

interface IImagesContext {
  closeFullscreenGallery: () => void;
  environmentImages: IImage[];
  measurementsImages: IImage[];
  fullscreenIndex: number;
  setFullscreenIndex: (index: number) => void;
  productImages: IImage[];
  setEnvironmentImages: (images: IImage[]) => void;
  setProductImages: (images: IImage[]) => void;
  setMeasurementsImages: (images: IImage[]) => void;
  setVideos: (videos: string[]) => void;
  videos: string[];
}

export const ImagesContext = createContext<IImagesContext | undefined>(undefined);

interface IImagesProvider {
  children: ReactNode;
}

const ImagesProvider = ({ children }: IImagesProvider) => {
  const [fullscreenIndex, setFullscreenIndex] = useState(-1);

  const {
    environmentImages: initialEnvironmentImages,
    productImages: initialProductImages,
    measurementsImages: initialMeasurementsImages,
    videos: initialVideos,
  } = useContext(ProductContext);

  const [productImages, setProductImages] = useState(initialProductImages);
  const [environmentImages, setEnvironmentImages] = useState(initialEnvironmentImages);
  const [measurementsImages, setMeasurementsImages] = useState(initialMeasurementsImages);
  const [videos, setVideos] = useState(initialVideos);

  useEffect(() => {
    setProductImages(initialProductImages);
    setEnvironmentImages(initialEnvironmentImages);
    setMeasurementsImages(initialMeasurementsImages);
    setVideos(initialVideos);
  }, [initialProductImages, initialEnvironmentImages, initialMeasurementsImages, initialVideos]);

  const closeFullscreenGallery = useCallback(() => {
    setFullscreenIndex(-1);
  }, []);

  const value = useMemo(
    () => ({
      closeFullscreenGallery,
      environmentImages,
      fullscreenIndex,
      productImages,
      setEnvironmentImages,
      setFullscreenIndex,
      setProductImages,
      setMeasurementsImages,
      measurementsImages,
      setVideos,
      videos,
    }),
    [productImages, environmentImages, fullscreenIndex, videos],
  );

  return <ImagesContext.Provider value={value}>{children}</ImagesContext.Provider>;
};

export default ImagesProvider;
